import type { HTMLAttributes, ReactNode, Ref } from 'react';

export type BadgeTone = 'accent' | 'neutral' | 'info' | 'success' | 'warning' | 'danger';
export type BadgeSize = 'sm' | 'md' | 'lg';
/** `soft` (default): tinted tone background + matching ink text.
 *  `solid`: filled tone color with contrast-paired foreground — stands out
 *           on accent tiles / tinted surfaces.
 *  `dot`:   no label, 8px coloured dot — pure status indicator.
 *           For labelled status badges ("● online"), use `soft` variant
 *           with `dot={true}` to prefix a small dot next to the label. */
export type BadgeVariant = 'soft' | 'solid' | 'dot';

export interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'className'> {
  tone?: BadgeTone;
  size?: BadgeSize;
  variant?: BadgeVariant;
  /** When true and `children` is provided, a small status dot is rendered
   *  before the label (labelled-dot pattern). When true and no children,
   *  acts as shorthand for `variant='dot'`. Ignored if `variant` is set
   *  to anything other than `soft`. */
  dot?: boolean;
  /** Accessible label — required when `variant='dot'` (the dot is decorative),
   *  recommended when children is a bare numeric ("3" → "3 unread notifications"). */
  label?: string;
  /** Hint that children is a numeric counter — enables tabular-nums + a
   *  symmetric min-width so "1" and "99" read as the same visual shape. */
  numeric?: boolean;
  className?: string;
  ref?: Ref<HTMLSpanElement>;
  children?: ReactNode;
}

/** Small status/count marker. Three visual modes:
 *  - `soft` (default): tinted background + tone-ink label
 *  - `solid`: filled tone — for counters on coloured tiles
 *  - `dot`:   pure status dot, no label (requires `label` for a11y)
 *
 *  Labelled status: use `soft` + `dot={true}` — `● online` pattern.
 *  Numeric counters: pass `numeric` so digits align and shape stays
 *  symmetric regardless of length.
 */
export function Badge(props: BadgeProps): ReactNode {
  const {
    tone = 'accent',
    size,
    variant,
    dot = false,
    label,
    numeric,
    className,
    ref,
    children,
    ...rest
  } = props;

  /* Resolve variant: explicit prop wins; else `dot` without children is
   * a pure dot; else default to soft. Labelled-dot pattern (dot=true +
   * children) stays as `soft` with the __dot sub-element prefixed. */
  const hasChildren = children !== undefined && children !== null && children !== '';
  const resolvedVariant: BadgeVariant = variant ?? (dot && !hasChildren ? 'dot' : 'soft');

  const classes = ['vq-badge', className].filter(Boolean).join(' ');
  const isDotOnly = resolvedVariant === 'dot';
  /* Empty visual node → hide from AT so it doesn't show up as a blank
   * badge entry for screen-reader users. */
  const isEmpty = isDotOnly && !hasChildren && label === undefined;
  const showDotPrefix = !isDotOnly && dot && hasChildren;

  return (
    <span
      {...rest}
      ref={ref}
      className={classes}
      data-component="Badge"
      data-tone={tone}
      data-variant={resolvedVariant}
      data-dot={isDotOnly ? 'true' : 'false'}
      data-numeric={numeric === true ? 'true' : undefined}
      data-size={size}
      role={label !== undefined ? 'status' : undefined}
      aria-label={label}
      aria-hidden={isEmpty ? 'true' : undefined}
    >
      {showDotPrefix ? <span className="vq-badge__dot" aria-hidden="true" /> : null}
      {isDotOnly ? null : children}
    </span>
  );
}
